import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { listMeetingSessions } from "../../api/Meetings/meetingSessionsApi";
import { formatMeetingListDate } from "../../utils/meetingListUtils";

/**
 * Elige reunión destino para mover fotos/vídeos seleccionados.
 */
export default function MeetingMoveMediaDialog({
  open,
  onClose,
  currentSessionId,
  selectedCount = 0,
  moving = false,
  onConfirm,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessions, setSessions] = useState([]);
  const [query, setQuery] = useState("");
  const [targetId, setTargetId] = useState(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setTargetId(null);
      setError("");
      setSessions([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    listMeetingSessions()
      .then((rows) => {
        if (cancelled) return;
        setSessions(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err.response?.data?.error ||
            err.message ||
            t("Meetings.moveMediaLoadError"),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  const filtered = useMemo(() => {
    const current = Number(currentSessionId);
    const q = String(query || "")
      .trim()
      .toLowerCase();
    return (sessions || [])
      .filter((s) => Number(s.id) !== current)
      .filter((s) => {
        if (!q) return true;
        const hay = [
          s.title,
          s.customerName,
          s.meetingDate,
          s.ownerUsername,
          String(s.id),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 80);
  }, [sessions, currentSessionId, query]);

  const selectedSession = useMemo(
    () => filtered.find((s) => Number(s.id) === Number(targetId)) || null,
    [filtered, targetId],
  );

  const handleConfirm = () => {
    if (!targetId || moving) return;
    onConfirm?.(Number(targetId));
  };

  return (
    <Dialog
      open={open}
      onClose={() => !moving && onClose?.()}
      fullWidth
      fullScreen={isNarrow}
      maxWidth="sm"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(15, 23, 42, 0.45)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: isNarrow ? 0 : 2.5,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ pr: 2, pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
              flexShrink: 0,
            }}
          >
            <DriveFileMoveOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
              {t("Meetings.moveMediaTitle")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.6, lineHeight: 1.45 }}
            >
              {t("Meetings.moveMediaBody", { count: selectedCount })}
            </Typography>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={t("Meetings.moveMediaCountChip", {
                count: selectedCount,
              })}
              sx={{ mt: 1, fontWeight: 700 }}
            />
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5, pb: 1 }}>
        <TextField
          autoFocus={!isNarrow}
          fullWidth
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("Meetings.moveMediaSearch")}
          disabled={moving || loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon
                  fontSize="small"
                  sx={{ color: "text.secondary" }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: alpha(theme.palette.action.hover, 0.35),
            },
          }}
        />

        {error ? (
          <Typography color="error" variant="body2" sx={{ mb: 1.25 }}>
            {error}
          </Typography>
        ) : null}

        {loading ? (
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              py: 5,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box
            sx={{
              py: 4,
              px: 2,
              textAlign: "center",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "divider",
              bgcolor: alpha(theme.palette.action.hover, 0.25),
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("Meetings.moveMediaEmpty")}
            </Typography>
          </Box>
        ) : (
          <Stack
            spacing={1}
            sx={{
              maxHeight: { xs: "48dvh", sm: 340 },
              overflowY: "auto",
              pr: 0.25,
            }}
          >
            {filtered.map((s) => {
              const selected = Number(targetId) === Number(s.id);
              const dateLabel = formatMeetingListDate(
                s.meetingDate || s.startedAt,
                i18n.language,
              );
              return (
                <Box
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  onClick={() => !moving && setTargetId(Number(s.id))}
                  onKeyDown={(e) => {
                    if (moving) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setTargetId(Number(s.id));
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    p: 1.35,
                    borderRadius: 2,
                    cursor: moving ? "default" : "pointer",
                    border: "1px solid",
                    borderColor: selected
                      ? alpha(theme.palette.primary.main, 0.55)
                      : "divider",
                    bgcolor: selected
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.background.paper, 0.9),
                    transition:
                      "border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease",
                    boxShadow: selected
                      ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
                      : "none",
                    "&:hover": moving
                      ? undefined
                      : {
                          borderColor: alpha(theme.palette.primary.main, 0.4),
                          bgcolor: selected
                            ? alpha(theme.palette.primary.main, 0.14)
                            : alpha(theme.palette.action.hover, 0.55),
                        },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: selected
                        ? alpha(theme.palette.primary.main, 0.18)
                        : alpha(theme.palette.text.primary, 0.06),
                      color: selected ? "primary.main" : "text.secondary",
                    }}
                  >
                    <EventOutlinedIcon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 800,
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {s.title || t("Meetings.untitled")}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mt: 0.25,
                        lineHeight: 1.35,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {[dateLabel, s.customerName].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                  {selected ? (
                    <CheckCircleRoundedIcon
                      color="primary"
                      sx={{ flexShrink: 0, fontSize: 22 }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor: "divider",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        {selectedSession ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1.25, lineHeight: 1.4 }}
          >
            {t("Meetings.moveMediaSelectedHint", {
              title: selectedSession.title || t("Meetings.untitled"),
            })}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 1.5,
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: 1,
          borderTop: "1px solid",
          borderColor: "divider",
          "& > :not(style)": { m: 0 },
        }}
      >
        <Button
          fullWidth={isNarrow}
          onClick={onClose}
          disabled={moving}
          sx={{ minHeight: 44 }}
        >
          {t("Meetings.cancel")}
        </Button>
        <Button
          fullWidth={isNarrow}
          color="primary"
          variant="contained"
          disabled={moving || !targetId}
          onClick={handleConfirm}
          startIcon={
            moving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DriveFileMoveOutlinedIcon />
            )
          }
          sx={{ minHeight: 48, fontWeight: 800 }}
        >
          {t("Meetings.moveMediaConfirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
